import { Helmet } from "react-helmet"
import {
    Avatar,
    Box,
    Button,
    Container,
    Divider,
    Grid,
    IconButton,
    MenuItem,
    Paper,
    Select,
    Typography,
} from "@mui/material"
import { makeStyles } from "@mui/styles"
import { useTranslation } from "react-i18next"
import { useNavigate, useSearchParams } from "react-router-dom"
import React, { useEffect, useMemo } from "react"
import { useSelector, useDispatch } from "react-redux"
import cssVariables from "../../../../theme/cssVariables"

import { ReactComponent as ChevronExpandDown } from "../../../../assets/icons/chevron-expand-down.svg"
import { ReactComponent as ChevronExpandUp } from "../../../../assets/icons/chevron-expand-up.svg"
import { ReactComponent as Nodata } from "../../../../assets/icons/nodata.svg"
import { ReactComponent as PencilIcon } from "../../../../icons/pencil.svg"

import CustomPagination from "../../../../components/CustomPagination"
import CustomTooltip from "../../../../components/CustomTooltip"

import * as Util from "../../../../services/global.service"
import {
    getEmailNotificationList,
} from "../../../../actions/admin/notification"

const useStyles = makeStyles({
    plusSvg: {
        "& > path": {
            fill: "#fff",
            stroke: "#fff",
        },
    },
    draggingListItem: {
        background: "rgb(235,235,235)",
    },
    toggleBtnRoot: {
        background: `transparent !important`,
        color: "#6B6B6B !important",
        width: "320px",
        border: "none !important",
    },
    toggleBtnSelected: {
        background: `${cssVariables.color.primary}!important`,
        color: "#fff!important",
    },
    flexPaper: {
        flex: 1,
        minWidth: 350,
        "box-shadow": "0px 2px 10px rgba(0, 0, 0, 0.1) !important",
        "border-radius": "10px !important        ",
    },
    tab: {
        fontSize: "16px !important",
        fontWeight: "300 !important",
        // color: "#A3A3A3 !important",
        "&.Mui-selected": {
            color: cssVariables.color.primary + " !important",
        },
    },
    root: {
        display: "flex",
        width: "100%",
        flexWrap: "wrap",
    },
})
const styles = {
    link: {
        color: "#5DA196",
        cursor: "pointer",
    },
    end: {
        color: "#A3A3A3",
    },
}

const EmailNotificationList = () => {
    const classes = useStyles()
    const { t } = useTranslation()
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const managerDashboardState = useSelector((state) => state.ManagerDashboard)
    const [openStatus, setOpenStatus] = React.useState(false)
    const [search, setSearch] = React.useState("")
    const [perPage, setPerPage] = React.useState(50)
    const [currentPage, setCurrentPage] = React.useState(1)
    const [totalPage, setTotalPage] = React.useState(1)
    const anchorRefStatus = React.useRef(null)
    const [searchParams] = useSearchParams()
    const [list, setList] = React.useState([])
    const [filteredList, setFilteredList] = React.useState([])

    const currentData = useMemo(() => {
        const firstPageIndex = (currentPage - 1) * perPage
        const lastPageIndex = firstPageIndex + perPage
        return filteredList.slice(firstPageIndex, lastPageIndex)
    }, [filteredList, currentPage, perPage])

    const handleTotalPage = (list) => {
        const List = [...list]
        const totalPage =
            Math.ceil(List.length / perPage) === 0
                ? 1
                : Math.ceil(List.length / perPage)
        setTotalPage(totalPage)
    }

    const handlePerPageChange = (event) => {
        setPerPage(event.target.value)
    }
    const handleSetCurrentPage = (value) => {
        const getSelectedPage = value.filter(
            (item) => item.type === "page" && item.selected
        )
        setCurrentPage(getSelectedPage.length > 0 ? getSelectedPage[0].page : 1)
    }
    let sortData = (key, direction) => {
        let result = [...filteredList]
        result = result.sort((a, b) => {
            if (direction) {
                return String(a[key]).localeCompare(String(b[key]))
            } else {
                return String(b[key]).localeCompare(String(a[key]))
            }
        })
        setFilteredList(result)
    }
    const onScroll = (evt) => {
        if (evt !== undefined && evt !== null) {
            if (openStatus) {
                setOpenStatus(false)
            }
        }
    }

    const fetchData = () => {
        dispatch(getEmailNotificationList()).then(resp => {
            if (resp) {
                resp = resp.sort((p, c) => String(p.feature).localeCompare(String(c.feature)))
                setFilteredList(resp)
            }
        })
    }

    // useEffect
    useEffect(() => {
        console.log("1")
        fetchData()
    }, [])

    useEffect(() => {
        handleTotalPage(filteredList)
    }, [filteredList, perPage])
    // end useEffect

    const columnWidths = [
        "calc(60%)",
        "calc(30%)",
        "calc(10%)",
    ]

    return (
        <Box
            onScroll={onScroll}
            style={{ maxHeight: "100%", overflowY: "auto" }}
        >
            <Helmet>
                <title>Email Notification Center | PMS </title>
            </Helmet>
            <Box
                sx={{
                    backgroundColor: "background.default",
                    minHeight: "100%",
                    py: 3,
                }}
            >
                <Container maxWidth="xl">
                    <Grid container mt={1} mb={1}>
                        <Typography fontSize={16} lineHeight={1} color="#68717A">
                            <span style={styles.end}>{t("emailNotificationCenter.label.title")}</span>
                        </Typography>
                    </Grid>
                    <Grid container mb={2}>
                        <Grid item display="flex" alignItems="center">
                            <Typography
                                color="#404349"
                                fontSize={36}
                                fontWeight="600"
                            >
                                {t("emailNotificationCenter.label.title")}
                            </Typography>
                        </Grid>
                    </Grid>
                    <Grid container spacing={2}>
                        <Grid container item>
                            <div className={classes.root}>
                                <Paper className={classes.flexPaper}>
                                    {/* TABLE HEADER */}
                                    <Box px="40px">
                                        <Box
                                            px="25px"
                                            py="19px"
                                            sx={{ display: "flex", flexDirection: "row" }}
                                        >
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    flexDirection: "row",
                                                    width: columnWidths[0],
                                                }}
                                                pr="15px"
                                            >
                                                <Typography
                                                    fontSize={16}
                                                    lineHeight="23.96px"
                                                    color="#A0A0A0"
                                                    fontWeight="300px"
                                                    sx={{
                                                        whiteSpace: "nowrap",
                                                        width: "100%",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        maxWidth: "max-content",
                                                        mr: "10px",
                                                    }}
                                                >
                                                    {t("emailNotificationCenter.label.feature")}
                                                </Typography>
                                                <Typography
                                                    component="span"
                                                    style={{
                                                        flexDirection: "column",
                                                        justifyContent: "center",
                                                        display: "flex",
                                                    }}
                                                >
                                                    <ChevronExpandUp
                                                        style={{ cursor: "pointer" }}
                                                        onClick={(e) => sortData("feature", true)}
                                                    />
                                                    <ChevronExpandDown
                                                        style={{ cursor: "pointer" }}
                                                        onClick={(e) => sortData("feature", false)}
                                                    />
                                                </Typography>
                                            </Box>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    flexDirection: "row",
                                                    width: columnWidths[1],
                                                }}
                                                pr="15px"
                                            >
                                                <Typography
                                                    fontSize={16}
                                                    lineHeight="23.96px"
                                                    color="#A0A0A0"
                                                    fontWeight="300px"
                                                    sx={{
                                                        whiteSpace: "nowrap",
                                                        width: "100%",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        maxWidth: "max-content",
                                                        mr: "10px",
                                                    }}
                                                >
                                                    {t("emailNotificationCenter.label.updated_by")}
                                                </Typography>
                                                <Typography
                                                    component="span"
                                                    style={{
                                                        flexDirection: "column",
                                                        justifyContent: "center",
                                                        display: "flex",
                                                    }}
                                                >
                                                    <ChevronExpandUp
                                                        style={{ cursor: "pointer" }}
                                                        onClick={(e) => sortData("updated_at", true)}
                                                    />
                                                    <ChevronExpandDown
                                                        style={{ cursor: "pointer" }}
                                                        onClick={(e) => sortData("updated_at", false)}
                                                    />
                                                </Typography>
                                            </Box>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    flexDirection: "row",
                                                    width: columnWidths[2],
                                                }}
                                                pr="15px"
                                            >
                                                <Typography
                                                    fontSize={16}
                                                    lineHeight="23.96px"
                                                    color="#A0A0A0"
                                                    fontWeight="300px"
                                                    sx={{
                                                        whiteSpace: "nowrap",
                                                        width: "100%",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        maxWidth: "max-content",
                                                        mr: "10px",
                                                    }}
                                                >
                                                    {t("emailNotificationCenter.label.action")}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Divider width="100%" />
                                    </Box>
                                    {currentData && currentData.length > 0 ? (
                                        currentData.map((row, index) => (
                                            <Box px="40px" key={`emp-${index}`}>
                                                <Box
                                                    sx={{
                                                        width: "100%",
                                                        position: "relative",
                                                        "&:before": {
                                                            content: '""',
                                                            position: "absolute",
                                                            height:
                                                                row?.status === "inactive" ? "100%" : "0px",
                                                            width:
                                                                row?.status === "inactive" ? "100%" : "0px",
                                                            top: "0",
                                                            background: "#FAFAFA",
                                                            opacity: "0.7",
                                                        },
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            height: "96px",
                                                            background: "#FFF",
                                                            width: "100%",
                                                            display: "flex",
                                                            alignItems: "center",
                                                        }}
                                                        px="25px"
                                                    >
                                                        <Box sx={{ width: columnWidths[0] }} pr="15px">
                                                            <Typography
                                                                fontSize={18}
                                                                lineHeight="23.96px"
                                                                fontWeight="500"
                                                                sx={{
                                                                    whiteSpace: "nowrap",
                                                                    width: "100%",
                                                                    overflow: "hidden",
                                                                    textOverflow: "ellipsis",
                                                                }}
                                                            >
                                                                {row?.feature ?? "-"}
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ width: columnWidths[1] }} pr="15px">
                                                            <Grid container spacing={2} flexWrap="nowrap">
                                                                {
                                                                    row?.updated_at && row?.updated_at != "" &&
                                                                    <>
                                                                        <Grid item display="flex" alignItems="center">
                                                                            <Avatar
                                                                                src={row?.updated_by?.photo}
                                                                            />
                                                                        </Grid>
                                                                        <Grid item>
                                                                            <Grid container>
                                                                                <Grid item display="flex" alignItems="center">
                                                                                    <Typography fontSize="16px" fontWeight="500">
                                                                                        {row?.updated_by?.fullname ?? "-"}
                                                                                    </Typography>
                                                                                </Grid>
                                                                            </Grid>
                                                                            <Grid container>
                                                                                <Grid item display="flex" alignItems="center">
                                                                                    <Typography fontSize="14px" fontWeight="400" color="#A3A3A3">
                                                                                        {row?.updated_at ? Util.dateDayTimeFormat(row?.updated_at) : "-"}
                                                                                    </Typography>
                                                                                </Grid>
                                                                            </Grid>
                                                                        </Grid>
                                                                    </>
                                                                }
                                                            </Grid>
                                                        </Box>
                                                        <Box
                                                            sx={{
                                                                width: columnWidths[2],
                                                                display: "flex",
                                                                justifyContent: "flex-start",
                                                                alignItems: "center",
                                                            }}
                                                            pr="15px"
                                                        >
                                                            <CustomTooltip title={t("label.edit")} placement="top">
                                                                <IconButton onClick={() => navigate(`/admin/email-notification-center/${row.serial}`)} disableRipple={true}>
                                                                    <PencilIcon />
                                                                </IconButton>
                                                            </CustomTooltip>

                                                        </Box>
                                                    </Box>
                                                </Box>
                                                <Divider width="100%" />
                                            </Box>
                                        ))
                                    ) : (
                                        <Box
                                            sx={{
                                                pt: "50px",
                                                textAlign: "center",
                                                pb: "500px",
                                            }}
                                        >
                                            <Nodata />
                                            <Typography
                                                color="#A0A0A0"
                                                fontWeight="500"
                                                fontSize={20}
                                                lineHeight="30px"
                                            >
                                                {t("label.noData")}
                                            </Typography>
                                        </Box>
                                    )}
                                </Paper>
                            </div>
                        </Grid>
                    </Grid>
                    <Grid container mt={1} spacing={2}>
                        <Grid item xs sm md display="flex" alignItems="center">
                            <Box mr={1}>
                                <Typography variant="s14_fw400">
                                    {t("label.showMaxItems")}
                                </Typography>
                            </Box>
                            <Select
                                id="pagination"
                                variant="outlined"
                                defaultValue={10}
                                value={perPage}
                                onChange={handlePerPageChange}
                                displayEmpty
                                size="small"
                                className="white-bg"
                            >
                                <MenuItem value={50}>
                                    <Typography style={{ fontWeight: "300" }}>50</Typography>
                                </MenuItem>
                                <MenuItem value={100}>
                                    <Typography style={{ fontWeight: "300" }}>100</Typography>
                                </MenuItem>
                                <MenuItem value={200}>
                                    <Typography style={{ fontWeight: "300" }}>200</Typography>
                                </MenuItem>
                            </Select>
                        </Grid>
                        <Grid item display="flex" alignItems="center" ml="auto">
                            <Typography variant="s14_fw400">
                                {t("label.page")} {currentPage} {t("label.of")} {totalPage}
                            </Typography>
                        </Grid>
                        <Grid item ml="auto" display="flex" alignItems="center">
                            <CustomPagination
                                setCurrentPage={handleSetCurrentPage}
                                selected={currentPage}
                                count={totalPage}
                            ></CustomPagination>
                        </Grid>
                    </Grid>
                </Container>
            </Box>
        </Box>
    )
}
export default EmailNotificationList
